import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Pencil, Trash2, Bell } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Vendors = () => {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [formData, setFormData] = useState({
    vendor_name: '',
    contact_person: '',
    phone: '',
    email: '',
    address: '',
    whatsapp_notifications: false
  });

  useEffect(() => {
    fetchVendors();
  }, []);

  const fetchVendors = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/vendors`, {
        withCredentials: true
      });
      setVendors(response.data);
    } catch (error) {
      toast.error('Failed to load vendors');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingVendor) {
        await axios.put(`${BACKEND_URL}/api/vendors/${editingVendor.vendor_id}`, formData, {
          withCredentials: true
        });
        toast.success('Vendor updated successfully');
      } else {
        await axios.post(`${BACKEND_URL}/api/vendors`, formData, {
          withCredentials: true
        });
        toast.success('Vendor created successfully');
      }
      fetchVendors();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error('Failed to save vendor');
    }
  };

  const handleDelete = async (vendorId) => {
    if (!window.confirm('Are you sure you want to delete this vendor?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/vendors/${vendorId}`, {
        withCredentials: true
      });
      toast.success('Vendor deleted successfully');
      fetchVendors();
    } catch (error) {
      toast.error('Failed to delete vendor');
    }
  };

  const openEditDialog = (vendor) => {
    setEditingVendor(vendor);
    setFormData({
      vendor_name: vendor.vendor_name,
      contact_person: vendor.contact_person || '',
      phone: vendor.phone,
      email: vendor.email || '',
      address: vendor.address || '',
      whatsapp_notifications: vendor.whatsapp_notifications || false
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingVendor(null);
    setFormData({ vendor_name: '', contact_person: '', phone: '', email: '', address: '' });
  };

  if (loading) {
    return <div className="text-stone-600">Loading vendors...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-stone-900 tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Vendors
          </h1>
          <p className="mt-2 text-base text-stone-600">Manage your vendor relationships</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button data-testid="create-vendor-btn" className="bg-amber-700 hover:bg-amber-800 text-white">
              <Plus className="w-4 h-4 mr-2" />
              Add Vendor
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white">
            <DialogHeader>
              <DialogTitle>{editingVendor ? 'Edit Vendor' : 'Add New Vendor'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="vendor_name">Vendor Name *</Label>
                <Input
                  id="vendor_name"
                  data-testid="vendor-name-input"
                  value={formData.vendor_name}
                  onChange={(e) => setFormData({ ...formData, vendor_name: e.target.value })}
                  required
                  className="border-stone-200 focus:ring-amber-700"
                />
              </div>
              <div>
                <Label htmlFor="contact_person">Contact Person</Label>
                <Input
                  id="contact_person"
                  data-testid="vendor-contact-input"
                  value={formData.contact_person}
                  onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                  className="border-stone-200 focus:ring-amber-700"
                />
              </div>
              <div>
                <Label htmlFor="phone">Phone *</Label>
                <Input
                  id="phone"
                  data-testid="vendor-phone-input"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  required
                  className="border-stone-200 focus:ring-amber-700"
                />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  data-testid="vendor-email-input"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="border-stone-200 focus:ring-amber-700"
                />
              </div>
              <div>
                <Label htmlFor="address">Address</Label>
                <Input
                  id="address"
                  data-testid="vendor-address-input"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="border-stone-200 focus:ring-amber-700"
                />
              </div>
              <div className="flex items-center justify-between py-2 px-1 rounded-lg bg-green-50 border border-green-200">
                <div className="flex items-center gap-2">
                  <Bell className="w-3.5 h-3.5 text-green-700" />
                  <Label className="text-xs text-green-800 font-medium cursor-pointer">WhatsApp Notifications</Label>
                </div>
                <Switch data-testid="vendor-whatsapp-toggle" checked={formData.whatsapp_notifications} onCheckedChange={v => setFormData({ ...formData, whatsapp_notifications: v })} />
              </div>
              <Button type="submit" data-testid="submit-vendor-btn" className="w-full bg-amber-700 hover:bg-amber-800 text-white">
                {editingVendor ? 'Update Vendor' : 'Create Vendor'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-stone-50 border-b border-stone-200">
              <tr>
                <th className="text-left px-6 py-3 text-xs uppercase tracking-wider font-medium text-stone-700">Vendor Name</th>
                <th className="text-left px-6 py-3 text-xs uppercase tracking-wider font-medium text-stone-700">Contact Person</th>
                <th className="text-left px-6 py-3 text-xs uppercase tracking-wider font-medium text-stone-700">Phone</th>
                <th className="text-left px-6 py-3 text-xs uppercase tracking-wider font-medium text-stone-700">Email</th>
                <th className="text-right px-6 py-3 text-xs uppercase tracking-wider font-medium text-stone-700">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-200">
              {vendors.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center text-stone-500">
                    No vendors found. Add your first vendor to get started.
                  </td>
                </tr>
              ) : (
                vendors.map((vendor) => (
                  <tr key={vendor.vendor_id} data-testid={`vendor-row-${vendor.vendor_id}`} className="hover:bg-stone-50 transition-colors duration-200">
                    <td className="px-6 py-4 font-medium text-stone-900">{vendor.vendor_name}</td>
                    <td className="px-6 py-4 text-stone-700">{vendor.contact_person || '-'}</td>
                    <td className="px-6 py-4 text-stone-700 tabular-nums">
                      {vendor.phone}
                      {vendor.whatsapp_notifications && <Bell data-testid={`vendor-whatsapp-bell-${vendor.vendor_id}`} className="w-3 h-3 inline ml-1.5 text-green-600" />}
                    </td>
                    <td className="px-6 py-4 text-stone-700">{vendor.email || '-'}</td>
                    <td className="px-6 py-4 text-right space-x-2">
                      <button
                        data-testid={`edit-vendor-${vendor.vendor_id}`}
                        onClick={() => openEditDialog(vendor)}
                        className="inline-flex items-center gap-1 text-amber-700 hover:text-amber-800 transition-colors duration-200"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        data-testid={`delete-vendor-${vendor.vendor_id}`}
                        onClick={() => handleDelete(vendor.vendor_id)}
                        className="inline-flex items-center gap-1 text-red-700 hover:text-red-800 transition-colors duration-200"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Vendors;